import createIdleScrollListener from "../utils/createIdleScrollListener";

function handleClassToggler(context, trigger, target) {
    const { toggleClass } = trigger.dataset;

    function onAboveViewport() {
        target.classList.add(toggleClass);
    }

    function removeClass() {
        target.classList.remove(toggleClass);
    }

    context.intersectionObserverProvider.addTask({
        onAboveViewport,
        onBellowViewport: removeClass,
        onIntersect: removeClass,
        rawNodes: [trigger],
    });
}

function debounce(handler, time) {
    let timeout;
    function runner() {
        if (timeout) {
            return;
        }

        timeout = setTimeout(function calling() {
            timeout = null;
            handler();
        }, time);
    }

    runner.cancel = function cancel() {
        if (timeout) {
            clearTimeout(timeout);
            timeout = null;
        }
    };

    return runner;
}

const DEFAULT_TRANSITION_MS = 33;
const DEFAULT_THRESHOLD = 0;

function handleStickyElement(_context, trigger, target) {
    const threshold =
        "offset" in trigger.dataset ? parseInt(trigger.dataset.offset, 10) : DEFAULT_THRESHOLD;
    const transition =
        "transition" in trigger.dataset
            ? parseFloat(trigger.dataset.transition, 10)
            : DEFAULT_TRANSITION_MS;

    const restingTranslate = `translate3d(0px, ${threshold}px, 0px)`;

    let windowHeight;
    let hiddenTranslate;
    let width;

    function onResize() {
        width = trigger.offsetWidth;
        windowHeight = window.innerHeight + 10;
        hiddenTranslate = `translate3d(0px, ${windowHeight}px, 0px)`;
        target.style.width = `${width}px`;
    }

    onResize();

    window.addEventListener("resize", onResize, { passive: true });

    function clamp(lowerLimit, upperLimit, number) {
        return Math.max(lowerLimit, Math.min(upperLimit, number));
    }

    function moveElement(top) {
        const distance = clamp(threshold, windowHeight, top);
        target.style.transition = `transform linear ${transition}ms`;
        target.style.transform = `translate3d(0px, ${distance}px, 0px)`;
    }

    function resetElement() {
        target.style.transform = restingTranslate;
        target.style.transition = "none";
    }

    function hideElement() {
        target.style.transition = "none";
        target.style.transform = hiddenTranslate;
    }

    function appearElement() {
        target.style.opacity = 1;
    }

    const { top } = trigger.getBoundingClientRect();

    let initiatior;

    if (top <= 0) {
        initiatior = resetElement;
    } else if (top > windowHeight) {
        initiatior = hideElement;
    } else {
        initiatior = moveElement;
    }

    initiatior(top);
    setTimeout(appearElement, 100);

    let previousAction = initiatior;
    function onScroll() {
        const { top } = trigger.getBoundingClientRect();
        let action;
        if (top <= 0) {
            action = resetElement;
        } else if (top >= windowHeight) {
            action = hideElement;
        }

        if (previousAction === action) {
            return;
        }

        if (!action) {
            action = moveElement;
        }
        action(top);
        previousAction = action;
    }

    const scrollHandler = debounce(createIdleScrollListener(onScroll), 16);
    window.addEventListener("scroll", scrollHandler, { passive: true });
}

function getHandler(triggerName) {
    if (triggerName === "sticky") {
        return handleStickyElement;
    }

    return handleClassToggler;
}

function getTargetElement(trigger) {
    const { target } = trigger.dataset;
    return document.querySelector(target);
}

export default function scrollTrigger(context, trigger) {
    const { triggers } = trigger.dataset;
    const target = getTargetElement(trigger);
    const handler = getHandler(triggers);
    handler(context, trigger, target);
}
